import React, { useEffect, useMemo, useState } from "react";
import { MapPin, Plus, X, Zap, Phone, Instagram, ArrowUpRight } from "lucide-react";
import { useSearchParams } from "react-router-dom";

const ExternalLinkIcon = () => <ArrowUpRight size={12} />;
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import ShareRideButton from "@/components/ShareRideButton";
import ShopPromoBanner from "@/components/ShopPromoBanner";
import SEO from "@/components/SEO";
import PageHeader from "@/components/PageHeader";

const LEVEL_OPTIONS = ["chill", "medium", "hard", "enduro"];
const STYLE_OPTIONS = ["flow", "tech", "race", "tour", "mixed"];

function formatDateLabel(iso, t, lang) {
    if (!iso) return "";
    const d = new Date(`${iso}T00:00:00`);
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today); tomorrow.setDate(today.getDate() + 1);
    if (d.getTime() === today.getTime()) return t("rideCall.today");
    if (d.getTime() === tomorrow.getTime()) return t("rideCall.tomorrow");
    return d.toLocaleDateString(lang === "it" ? "it-IT" : "en-GB", { weekday: "short", day: "2-digit", month: "short" });
}

/** Build "tap to contact" link from host_contact + contact_type. */
function buildContactHref(ride) {
    if (!ride?.host_contact) return null;
    if (ride.contact_type === "whatsapp") {
        const digits = String(ride.host_contact).replace(/\D/g, "");
        return `https://wa.me/${digits}`;
    }
    if (ride.contact_type === "instagram") {
        const handle = String(ride.host_contact).replace(/^@/, "");
        return `https://instagram.com/${handle}`;
    }
    return null;
}

function RideCard({ ride, trailMap, comboMap, t, lang }) {
    const isCombo = !!ride.combo_slug;
    const isCustom = Array.isArray(ride.trail_slugs) && ride.trail_slugs.length > 0 && !ride.trail_slug && !ride.combo_slug;
    const combo = isCombo ? comboMap[ride.combo_slug] : null;
    const isEvent = ride.event === "tes";
    const trailName = isCombo
        ? (combo ? ((lang === "en" && combo.name_en) || combo.name) : ride.combo_slug)
        : isCustom
            ? ride.trail_slugs.map((s) => trailMap[s]?.name || s).join(" → ")
            : (trailMap[ride.trail_slug]?.name || ride.trail_slug || (isEvent ? (lang === "en" ? "Pre-race recon · official course" : "Giro pregara · percorso ufficiale") : ""));
    const stepSlugs = isCombo ? (combo?.trail_slugs || []) : (isCustom ? ride.trail_slugs : []);
    const contactHref = buildContactHref(ride);
    const isWA = ride.contact_type === "whatsapp";

    return (
        <article data-testid={`ride-card-${ride.id}`}
            className="bg-zinc-950 border border-white/10 hover:border-abnormal-acid transition-colors p-6 flex flex-col gap-4">
            <div className="flex items-start justify-between gap-3">
                <div className="flex flex-col">
                    <p className="text-[10px] tracking-[0.3em] uppercase text-abnormal-acid font-bold mb-1">
                        {formatDateLabel(ride.date, t, lang)} · {ride.time}
                        {isEvent && <span data-testid={`ride-event-badge-${ride.id}`} className="ml-2 bg-abnormal-magenta text-black px-1.5 py-0.5">PREGARA · TES</span>}
                    </p>
                    <h3 className="font-display text-2xl uppercase leading-tight">{trailName}</h3>
                    {stepSlugs.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2 text-[10px] uppercase tracking-wider text-zinc-400">
                            {stepSlugs.map((slug, idx) => (
                                <React.Fragment key={`${ride.id}-${slug}-${idx}`}>
                                    <span className="border border-white/10 px-1.5 py-0.5">{slug}</span>
                                    {idx < stepSlugs.length - 1 && <span className="text-abnormal-acid">→</span>}
                                </React.Fragment>
                            ))}
                        </div>
                    )}
                </div>
                <span className="text-[10px] tracking-[0.3em] uppercase border border-zinc-700 text-zinc-400 font-bold px-2 py-1">
                    {ride.spots_total} {t("rideCall.spotsLeft")}
                </span>
            </div>

            <div className="grid grid-cols-2 gap-3 text-[11px] uppercase tracking-wider text-zinc-400">
                <div className="flex items-center gap-2"><Zap size={12} className="text-abnormal-acid" /><b className="text-zinc-200">{t(`rideCall.levels.${ride.level}`)}</b></div>
                <div className="flex items-center gap-2"><span className="w-1.5 h-1.5 bg-abnormal-acid" /><b className="text-zinc-200">{t(`rideCall.styles.${ride.style}`)}</b></div>
            </div>

            <div className="flex items-start gap-2 text-sm text-zinc-300">
                <MapPin size={14} className="mt-1 text-abnormal-acid flex-shrink-0" />
                <span>{ride.meeting_point}</span>
            </div>

            {ride.notes && <p className="text-sm text-zinc-400 leading-relaxed border-l-2 border-white/10 pl-3">{ride.notes}</p>}

            <div className="border-t border-white/10 pt-3 text-xs text-zinc-400 uppercase tracking-wider">
                {t("rideCall.host")}: <span className="text-zinc-200">{ride.host_name}</span>
            </div>

            <div className="flex flex-wrap gap-2 pt-1">
                {contactHref && (
                    <a href={contactHref} target="_blank" rel="noreferrer"
                        data-testid={`ride-join-${ride.id}`}
                        className="text-xs uppercase tracking-[0.2em] font-bold bg-abnormal-acid text-black px-4 py-2 hover:bg-abnormal-paper hover:text-black transition-colors inline-flex items-center gap-2">
                        {isWA ? <Phone size={12} /> : <Instagram size={12} />}
                        {t("rideCall.contact")} {isWA ? "WhatsApp" : "Instagram"}
                    </a>
                )}
                <ShareRideButton ride={ride} trailName={trailName} isFull={false} spotsLeft={ride.spots_total} />
            </div>
        </article>
    );
}

function NewRideForm({ trails, combos, onCreated, onCancel, t, preset = null, routeUrl = "" }) {
    const [busy, setBusy] = useState(false);
    const today = new Date().toISOString().slice(0, 10);
    const isEvent = preset?.event === "tes";
    const [mode, setMode] = useState(isEvent ? "event" : "trail");  // trail | combo | custom | event
    const [form, setForm] = useState({
        host_name: "",
        contact_type: "whatsapp",
        host_contact: "",
        date: isEvent ? "2026-09-27" : today, time: "09:00",
        target: "", customSlugs: [], customAdd: "",
        meeting_point: isEvent ? "Piazzale dei Rioni, Porto Santo Stefano" : "", level: "medium", style: isEvent ? "race" : "flow",
        spots_total: 6, notes: isEvent ? "Giro pregara Toscano Enduro Series · ricognizione del percorso ufficiale." : "",
    });

    const reset = () => setForm({
        host_name: "", contact_type: "whatsapp", host_contact: "",
        date: today, time: "09:00", target: "", customSlugs: [], customAdd: "",
        meeting_point: "", level: "medium", style: "flow",
        spots_total: 6, notes: "",
    });

    const submit = async (e) => {
        e.preventDefault();
        if (!form.host_name.trim()) return toast.error(t("rideCall.errNoName"));
        if (!form.host_contact.trim()) return toast.error(t("rideCall.errNoContact"));
        if (!form.meeting_point.trim()) return toast.error(t("rideCall.errNoMeet"));
        const payload = {
            host_name: form.host_name.trim(),
            contact_type: form.contact_type,
            host_contact: form.host_contact.trim(),
            date: form.date, time: form.time,
            meeting_point: form.meeting_point.trim(),
            level: form.level, style: form.style,
            spots_total: Number(form.spots_total) || 1,
            notes: form.notes.trim(),
            trail_slug: "", combo_slug: "", trail_slugs: [],
            event: isEvent ? "tes" : "",
        };
        if (mode === "event") {
            /* route = official course, no trail selection needed */
        } else if (mode === "trail") {
            if (!form.target.startsWith("trail:")) return toast.error(t("rideCall.errNoTrail"));
            payload.trail_slug = form.target.replace("trail:", "");
        } else if (mode === "combo") {
            if (!form.target.startsWith("combo:")) return toast.error(t("rideCall.errNoTrail"));
            payload.combo_slug = form.target.replace("combo:", "");
        } else {
            if ((form.customSlugs || []).length < 2) return toast.error(t("rideCall.errCustomMin"));
            payload.trail_slugs = form.customSlugs;
        }
        setBusy(true);
        try {
            await api.post("/rides", payload);
            toast.success(t("rideCall.created"));
            reset();
            onCreated();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Errore");
        } finally {
            setBusy(false);
        }
    };

    const fld = "w-full bg-zinc-950 border border-zinc-700 px-3 py-2 text-white focus:border-abnormal-acid outline-none";
    const lbl = "text-[10px] uppercase tracking-[0.25em] font-bold text-zinc-400 block mb-1";

    return (
        <form onSubmit={submit} data-testid="ride-form"
            className="bg-zinc-950 border border-abnormal-acid/40 p-5 md:p-6 space-y-5">
            <div className="flex items-center justify-between">
                <h3 className="font-display text-2xl uppercase">{t("rideCall.newRide")}</h3>
                <button type="button" onClick={onCancel} data-testid="ride-form-cancel"
                    className="text-zinc-400 hover:text-white"><X size={18} /></button>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
                <div>
                    <label className={lbl}>{t("rideCall.yourName")} *</label>
                    <input data-testid="ride-host-name" value={form.host_name}
                        onChange={(e) => setForm({ ...form, host_name: e.target.value })}
                        className={fld} maxLength={60} placeholder="Mario" required />
                </div>
                <div>
                    <label className={lbl}>{t("rideCall.contactLabel")} *</label>
                    <div className="flex gap-2">
                        <select data-testid="ride-contact-type" value={form.contact_type}
                            onChange={(e) => setForm({ ...form, contact_type: e.target.value })}
                            className={`${fld} flex-shrink-0 w-auto`}>
                            <option value="whatsapp">WhatsApp</option>
                            <option value="instagram">Instagram</option>
                        </select>
                        <input data-testid="ride-host-contact" value={form.host_contact}
                            onChange={(e) => setForm({ ...form, host_contact: e.target.value })}
                            className={fld} placeholder={form.contact_type === "whatsapp" ? "+39 333 1234567" : "@tuo_handle"} required />
                    </div>
                </div>
            </div>

            <div className="flex gap-2 flex-wrap">
                {(isEvent ? [["event", "Percorso ufficiale TES"]] : [
                    ["trail", t("rideCall.modeTrail")],
                    ["combo", t("rideCall.modeCombo")],
                    ["custom", t("rideCall.modeCustom")],
                ]).map(([k, label]) => (
                    <button key={k} type="button"
                        data-testid={`ride-mode-${k}`}
                        onClick={() => setForm({ ...form, target: "", customSlugs: [] }) || setMode(k)}
                        className={`text-xs uppercase tracking-[0.2em] font-bold px-3 py-2 border ${mode === k ? "bg-abnormal-acid text-black border-abnormal-acid" : "border-zinc-700 text-zinc-300 hover:border-white"}`}>
                        {label}
                    </button>
                ))}
            </div>

            {mode === "event" && (
                <div data-testid="ride-event-route" className="border border-abnormal-magenta/50 bg-abnormal-charcoal p-4 flex items-center justify-between gap-3 flex-wrap">
                    <div>
                        <p className="label-mono text-abnormal-magenta">PREGARA · TOSCANO ENDURO SERIES · 04.10.2026</p>
                        <p className="font-display uppercase text-xl leading-none mt-1 text-abnormal-paper">Ricognizione del percorso ufficiale</p>
                    </div>
                    {routeUrl ? (
                        <a href={routeUrl} target="_blank" rel="noreferrer" data-testid="ride-event-route-link" className="btn-ghost !py-2 !px-3 !text-[11px]">Percorso ufficiale <ExternalLinkIcon /></a>
                    ) : (
                        <span className="label-mono text-abnormal-paperDim/60">Percorso ufficiale in arrivo</span>
                    )}
                </div>
            )}
            {mode !== "custom" && mode !== "event" && (
                <div>
                    <label className={lbl}>{t("rideCall.trail")} *</label>
                    <select data-testid="ride-target" value={form.target}
                        onChange={(e) => setForm({ ...form, target: e.target.value })}
                        className={fld} required>
                        <option value="">{t("rideCall.selectTrail")}</option>
                        {mode === "trail" && trails.map((tr) => (
                            <option key={tr.slug} value={`trail:${tr.slug}`}>{tr.name}</option>
                        ))}
                        {mode === "combo" && combos.map((c) => (
                            <option key={c.slug} value={`combo:${c.slug}`}>{c.name}</option>
                        ))}
                    </select>
                </div>
            )}

            {mode === "custom" && (
                <div>
                    <label className={lbl}>{t("rideCall.customRoute")} *</label>
                    <div className="flex flex-wrap gap-2 mb-2 min-h-[40px] p-2 border border-dashed border-zinc-700 bg-abnormal-charcoal">
                        {form.customSlugs.length === 0 && <span className="text-zinc-500 text-xs italic">{t("rideCall.customEmpty")}</span>}
                        {form.customSlugs.map((s, i) => (
                            <span key={`${s}-${i}`} className="bg-abnormal-acid text-black px-2 py-1 text-xs uppercase inline-flex items-center gap-2">
                                {i + 1}. {trails.find((x) => x.slug === s)?.name || s}
                                <button type="button" onClick={() => setForm({ ...form, customSlugs: form.customSlugs.filter((_, idx) => idx !== i) })}
                                    className="text-white hover:text-black"><X size={10} /></button>
                            </span>
                        ))}
                    </div>
                    <div className="flex gap-2">
                        <select data-testid="ride-custom-add" value={form.customAdd}
                            onChange={(e) => setForm({ ...form, customAdd: e.target.value })}
                            className={fld}>
                            <option value="">{t("rideCall.addTrail")}…</option>
                            {trails.filter((x) => !form.customSlugs.includes(x.slug)).map((tr) => (
                                <option key={tr.slug} value={tr.slug}>{tr.name}</option>
                            ))}
                        </select>
                        <button type="button" data-testid="ride-custom-add-btn"
                            disabled={!form.customAdd}
                            onClick={() => setForm({ ...form, customSlugs: [...form.customSlugs, form.customAdd], customAdd: "" })}
                            className="bg-abnormal-acid text-black px-3 py-2 disabled:opacity-50 text-xs uppercase tracking-wider font-bold flex-shrink-0">
                            <Plus size={14} />
                        </button>
                    </div>
                </div>
            )}

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div>
                    <label className={lbl}>{t("rideCall.date")}</label>
                    <input data-testid="ride-date" type="date" value={form.date}
                        min={today} onChange={(e) => setForm({ ...form, date: e.target.value })}
                        className={fld} required />
                </div>
                <div>
                    <label className={lbl}>{t("rideCall.time")}</label>
                    <input data-testid="ride-time" type="time" value={form.time}
                        onChange={(e) => setForm({ ...form, time: e.target.value })}
                        className={fld} required />
                </div>
                <div>
                    <label className={lbl}>{t("rideCall.level")}</label>
                    <select data-testid="ride-level" value={form.level}
                        onChange={(e) => setForm({ ...form, level: e.target.value })}
                        className={fld}>
                        {LEVEL_OPTIONS.map((l) => <option key={l} value={l}>{t(`rideCall.levels.${l}`)}</option>)}
                    </select>
                </div>
                <div>
                    <label className={lbl}>{t("rideCall.style")}</label>
                    <select data-testid="ride-style" value={form.style}
                        onChange={(e) => setForm({ ...form, style: e.target.value })}
                        className={fld}>
                        {STYLE_OPTIONS.map((s) => <option key={s} value={s}>{t(`rideCall.styles.${s}`)}</option>)}
                    </select>
                </div>
            </div>

            <div className="grid sm:grid-cols-[1fr_auto] gap-3">
                <div>
                    <label className={lbl}>{t("rideCall.meetingPoint")} *</label>
                    <input data-testid="ride-meeting" value={form.meeting_point}
                        onChange={(e) => setForm({ ...form, meeting_point: e.target.value })}
                        className={fld} maxLength={160} placeholder={t("rideCall.meetingPh")} required />
                </div>
                <div>
                    <label className={lbl}>{t("rideCall.spots")}</label>
                    <input data-testid="ride-spots" type="number" min={1} max={30} value={form.spots_total}
                        onChange={(e) => setForm({ ...form, spots_total: e.target.value })}
                        className={`${fld} w-24`} />
                </div>
            </div>

            <div>
                <label className={lbl}>{t("rideCall.notes")}</label>
                <textarea data-testid="ride-notes" value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    className={`${fld} min-h-[70px]`} maxLength={600} placeholder={t("rideCall.notesPh")} />
            </div>

            <div className="flex gap-3 pt-2">
                <button type="submit" disabled={busy} data-testid="ride-submit"
                    className="flex-1 bg-abnormal-acid text-black font-bold uppercase tracking-[0.25em] text-xs px-5 py-3 hover:bg-abnormal-paper hover:text-black border-2 border-abnormal-acid transition-colors disabled:opacity-60">
                    {busy ? t("rideCall.publishing") : t("rideCall.publishRide")}
                </button>
                <button type="button" onClick={onCancel} data-testid="ride-cancel"
                    className="text-xs uppercase tracking-[0.25em] font-bold border border-zinc-700 text-zinc-400 px-5 py-3 hover:border-white hover:text-white">
                    {t("rideCall.cancel")}
                </button>
            </div>
        </form>
    );
}

export default function RideCallPage() {
    const [rides, setRides] = useState([]);
    const [trails, setTrails] = useState([]);
    const [combos, setCombos] = useState([]);
    const [params] = useSearchParams();
    const preset = params.get("event") === "tes" ? { event: "tes" } : null;
    const [showForm, setShowForm] = useState(!!preset || params.get("open") === "1");
    const [routeUrl, setRouteUrl] = useState("");
    const { t, lang } = useLang();

    const load = async () => {
        try {
            const [ri, tr, co] = await Promise.all([
                api.get("/rides"),
                api.get("/trails"),
                api.get("/combos"),
            ]);
            setRides(ri.data || []);
            setTrails(tr.data || []);
            setCombos(co.data || []);
        } catch (e) { console.error("RideCall load failed:", e); }
    };
    useEffect(() => { load(); api.get("/settings").then((r) => setRouteUrl(r.data?.tes_route_url || "")).catch(() => {}); }, []);

    const trailMap = useMemo(() => Object.fromEntries(trails.map((tr) => [tr.slug, tr])), [trails]);
    const comboMap = useMemo(() => Object.fromEntries(combos.map((c) => [c.slug, c])), [combos]);

    return (
        <div data-testid="ride-call-page" className="bg-abnormal-ink">
            <SEO
                title={lang === "en" ? "Ride Call · Community Rides" : "Ride Call · Giri community"}
                description={lang === "en"
                    ? "Publish your next ride or contact other riders directly via WhatsApp/Instagram. No login, no friction."
                    : "Pubblica il tuo prossimo giro o contatta altri rider direttamente via WhatsApp/Instagram. Niente login, niente attrito."}
                path="/ride-call"
            />

            <PageHeader
                label="RIDES · RIDE CALL"
                title={t("rideCall.title1")}
                accent={t("rideCall.title2")}
                tagline={[lang === "en" ? "No login" : "Senza login", "WhatsApp · Instagram", "Monte Argentario"]}
                sub={t("rideCall.lead")}
                right={
                    <button data-testid="ride-new-cta" onClick={() => setShowForm((v) => !v)} className="btn-primary">
                        <Plus size={14} /> {showForm ? t("rideCall.closeForm") : t("rideCall.newRide")}
                    </button>
                }
            />
            <div className="max-w-[1400px] mx-auto px-5 md:px-10 pb-14 md:pb-20">

            {showForm && (
                <div className="mt-10">
                    <NewRideForm trails={trails} combos={combos} preset={preset} routeUrl={routeUrl}
                        onCreated={() => { setShowForm(false); load(); }}
                        onCancel={() => setShowForm(false)} t={t} />
                </div>
            )}

            <section className="mt-12">
                <h2 className="font-display text-2xl md:text-3xl uppercase mb-6">{t("rideCall.upcoming")}</h2>
                {rides.length === 0 ? (
                    <p data-testid="ride-empty" className="text-zinc-500 italic">{t("rideCall.empty")}</p>
                ) : (
                    <div className="grid md:grid-cols-2 gap-5">
                        {rides.map((r) => (
                            <RideCard key={r.id} ride={r} trailMap={trailMap} comboMap={comboMap} t={t} lang={lang} />
                        ))}
                    </div>
                )}
            </section>

            <section className="mt-12">
                <ShopPromoBanner testidPrefix="ride-shop-promo" />
            </section>
            </div>
        </div>
    );
}
